/*
  Anchor-link handling for the mobile menu drawer.

  Dawn's drawer relies on a full page load to tear itself down, so any menu link
  pointing at an #id breaks on mobile:

  1. Same-page hash links never navigate, so the drawer stays open and
     <body> keeps `overflow-hidden-*` -> the page cannot scroll at all.
  2. Cross-page hash links land underneath the sticky header, and lazy-loaded
     content below the fold shifts the target after the browser has jumped.
  3. Returning via the back button restores the page from the bfcache with the
     drawer still open and scrolling still locked.

  Desktop is unaffected because it uses the dropdown/mega menu, not the drawer.
*/
(function () {
  'use strict';

  var HEADER_GAP = 16;

  function headerOffset() {
    var stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader) return 0; // Header does not overlay content.
    return stickyHeader.offsetHeight + HEADER_GAP;
  }

  function targetFromHash(hash) {
    if (!hash || hash === '#') return null;
    var id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (e) {
      /* leave the raw value */
    }
    return document.getElementById(id) || document.getElementsByName(id)[0] || null;
  }

  // Returns the target element when the link points at the page we are already
  // on, otherwise null (the browser should navigate normally).
  function sameDocumentTarget(link) {
    if (!link.hash) return null;
    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return null;
    }
    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== window.location.pathname) return null;
    if (url.search !== window.location.search) return null;
    return targetFromHash(url.hash);
  }

  function scrollToTarget(element, behavior) {
    var top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    if (top < 0) top = 0;
    try {
      window.scrollTo({ top: top, behavior: behavior || 'smooth' });
    } catch (e) {
      // Safari < 15.4 rejects behavior: 'instant'.
      window.scrollTo(0, top);
    }
  }

  // Whatever else happens, scrolling must be unlocked -- a stuck
  // `overflow-hidden-*` on <body> is what makes the page feel frozen. Dawn
  // removes these synchronously too, so this does not disturb the slide-out.
  function unlockScroll() {
    document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
    var header = document.querySelector('.section-header');
    if (header) header.classList.remove('menu-open');
  }

  // Runs only if the drawer is still open once the slide-out should have
  // finished, i.e. Dawn's own teardown did not complete.
  function hardTeardown(drawer) {
    var details = drawer.querySelector('details');
    if (!details || !details.hasAttribute('open')) return;

    details.classList.remove('menu-opening');
    details.querySelectorAll('details').forEach(function (child) {
      child.removeAttribute('open');
      child.classList.remove('menu-opening');
    });
    drawer.querySelectorAll('.submenu-open').forEach(function (submenu) {
      submenu.classList.remove('submenu-open');
    });
    details.removeAttribute('open');
    unlockScroll();
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    try {
      if (typeof drawer.closeMenuDrawer === 'function') {
        // HeaderDrawer bails out unless both arguments are provided.
        drawer.closeMenuDrawer(new Event('click'), drawer.querySelector('summary'));
      }
    } catch (e) {
      /* the safety net below still runs */
    }
    unlockScroll();
    setTimeout(function () {
      hardTeardown(drawer);
    }, 500);
  }

  // Force every drawer shut without needing a click to originate inside one.
  function forceCloseDrawers() {
    document.querySelectorAll('header-drawer, menu-drawer').forEach(function (drawer) {
      var details = drawer.querySelector('details');
      if (details && details.hasAttribute('open')) closeDrawer(drawer);
    });
  }

  document.addEventListener('click', function (event) {
    if (!event.target || typeof event.target.closest !== 'function') return;
    var link = event.target.closest('a[href]');
    if (!link) return;
    var drawer = link.closest('header-drawer, menu-drawer');
    if (!drawer) return;
    if (link.target && link.target !== '_self') return;

    var target = sameDocumentTarget(link);

    // Cross-page link: close the drawer so the back button cannot restore a
    // locked page from the bfcache, then let the browser navigate.
    if (!target) {
      closeDrawer(drawer);
      return;
    }

    // Same-page link: the browser will not navigate, so do the work ourselves.
    event.preventDefault();
    closeDrawer(drawer);

    requestAnimationFrame(function () {
      scrollToTarget(target, 'smooth');
      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', link.hash);
      }
    });
  });

  // Re-align a cross-page anchor landing: the native jump happens before the
  // sticky header is measured and before lazy content below settles.
  function realignOnLoad() {
    var target = targetFromHash(window.location.hash);
    if (!target) return;

    var interrupted = false;
    var stop = function () {
      interrupted = true;
    };
    ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
      window.addEventListener(type, stop, { passive: true, once: true });
    });

    var align = function () {
      if (interrupted) return;
      scrollToTarget(target, 'instant');
    };

    requestAnimationFrame(align);
    setTimeout(align, 300);
    window.addEventListener(
      'load',
      function () {
        setTimeout(align, 100);
      },
      { once: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', realignOnLoad, { once: true });
  } else {
    realignOnLoad();
  }

  // iOS restores the previous page from the bfcache with the drawer still open.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) forceCloseDrawers();
  });
})();
